<script setup>
import { ref, computed, onMounted } from 'vue'
import { DxResource, DxScheduler } from 'devextreme-vue/scheduler'

import dayjs from '@/plugins/dayjs'
import { getI18n } from '@/plugins/i18n'

import { useODataSource } from '@/composables/data-grid'
import { useDetectDevice } from '@/composables/device'
import { usePlanningStore, VACATION_PROJECT_ID } from '@/stores/planning.store'
import { useAccountingStore } from '@/stores/accounting.store'

import { scheduleApi } from '@/apis/schedule.api'

const READONLY_EDITING = {
  allowAdding: false,
  allowDeleting: false,
  allowUpdating: false,
  allowDragging: false,
  allowResizing: false,
}

const props = defineProps({
  service: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const planningStore = usePlanningStore()
const accountingStore = useAccountingStore()
const { isMobile } = useDetectDevice()

const calendarHeight = computed(() => (isMobile.value ? 'calc(100vh - 13rem)' : '26rem'))

const dataSource = ref(null)
const projectResources = ref([])
const schedulerViews = ['workWeek', 'week', 'month']
const currentView = ref('month')

const schedulerStartHour = computed(() => (accountingStore.companyData?.start_scheduler_time
  ? parseInt(accountingStore.companyData.start_scheduler_time.split(':')[0])
  : 0))
const schedulerEndHour = computed(() => (accountingStore.companyData?.end_scheduler_time
  ? parseInt(accountingStore.companyData.end_scheduler_time.split(':')[0])
  : 24))
const schedulerCellDuration = computed(() => accountingStore.companyData?.scheduler_cell_duration || 30)

const resourceIdFor = (event) => event.project_id ?? VACATION_PROJECT_ID
const projectFor = (event) => (planningStore.isVacationProject(event.project_id)
  ? planningStore.getVacationProject()
  : planningStore.getProjectById(event.project_id))

const groupKey = (event) => (event.budget_document_row_id != null
  ? `row-${event.budget_document_row_id}`
  : `vac-${resourceIdFor(event)}`)

const mergeContiguous = (events) => {
  const groups = {}
  events.forEach(event => (groups[groupKey(event)] ??= []).push(event))

  const merged = []
  Object.entries(groups).forEach(([key, groupEvents]) => {
    const sorted = groupEvents.slice().sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf())
    let run = null
    sorted.forEach(event => {
      if (run && dayjs(event.start).isSameOrBefore(dayjs(run.end).add(1, 'day'), 'day')) {
        if (dayjs(event.end).isAfter(dayjs(run.end))) run.end = event.end
        run.confirmed = run.confirmed && event.confirmed
      } else {
        run = {
          id: `${key}-${event.id}`,
          start: event.start,
          end: event.end,
          confirmed: event.confirmed,
          resource_id: resourceIdFor(event),
          project: projectFor(event),
        }
        merged.push(run)
      }
    })
  })
  return merged
}

onMounted(async() => {
  if (!planningStore.projects?.length) await planningStore.fetchProjectsAsync({ all: true }, true)
  if (!accountingStore.companyData?.id) await accountingStore.fetchCompanyDataAsync()
  projectResources.value = [planningStore.getVacationProject(), ...planningStore.projects]

  dataSource.value = useODataSource(scheduleApi.getAbsoluteUrl() + scheduleApi.eventsUrl, {
    customParamsFn: () => ({ service_ids: [props.service.id] }),
    postProcess: mergeContiguous,
  })
})

const dayTime = (value) => dayjs(value).format('HH:mm')

const onAppointmentRendered = (e) => {
  e.appointmentElement.classList.toggle('availability-appointment--unconfirmed', !e.appointmentData.confirmed)
}
</script>

<template>
  <div class="availability-calendar">
    <dx-scheduler
      v-if="dataSource"
      v-model:current-view="currentView"
      text-expr="project.name"
      start-date-expr="start"
      end-date-expr="end"
      key-expr="id"
      :height="calendarHeight"
      remote-filtering
      all-day-panel-mode="hidden"
      :start-day-hour="schedulerStartHour"
      :end-day-hour="schedulerEndHour"
      :cell-duration="schedulerCellDuration"
      :current-date="new Date()"
      :views="schedulerViews"
      :first-day-of-week="1"
      :data-source="dataSource"
      :editing="READONLY_EDITING"
      appointment-tooltip-template="TooltipSlot"
      @appointment-dbl-click="e => e.cancel = true"
      @appointment-rendered="onAppointmentRendered"
    >
      <template #TooltipSlot="{ data }">
        <div class="q-pa-sm">
          <div class="text-weight-medium">{{ data.appointmentData.project?.name }}</div>
          <div class="text-caption">
            {{ dayTime(data.appointmentData.start) }}–{{ dayTime(data.appointmentData.end) }}
          </div>
          <q-chip
            dense
            size="sm"
            :color="data.appointmentData.confirmed ? 'positive' : 'warning'"
            text-color="white"
            :label="data.appointmentData.confirmed ? i18n.t('common.confirmed') : i18n.t('common.notConfirmed')"
          />
        </div>
      </template>
      <dx-resource
        :data-source="projectResources"
        :allow-multiple="false"
        :use-color-as-default="true"
        field-expr="resource_id"
        label="Project"
        display-expr="name"
        color-expr="color"
      />
    </dx-scheduler>
  </div>
</template>

<style scoped>
.availability-calendar :deep(.dx-scheduler-cell-sizes-vertical) {
  height: 1.6rem !important;
}
.availability-calendar :deep(.dx-scheduler-date-table-cell) {
  height: 2rem;
}
.availability-calendar :deep(.availability-appointment--unconfirmed) {
  opacity: 0.45;
  background-image:
    repeating-linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.35),
      rgba(255, 255, 255, 0.05) 4px,
      transparent 4px,
      transparent 9px
    ) !important;
}
</style>
